import React from 'react';
import {
  ClockIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  PhotoIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { Listing as Property } from '../../property/property-card/PropertyCardTypes';

interface PropertyMaintenanceSummaryCardProps {
  property: Property;
  requestsCount: number; // Total requests
  pendingCount: number;
  inProgressCount: number;
  onClick: () => void;
}

const PropertyMaintenanceSummaryCard: React.FC<
  PropertyMaintenanceSummaryCardProps
> = ({ property, requestsCount, pendingCount, inProgressCount, onClick }) => {
  const completedCount = Math.max(
    0,
    requestsCount - pendingCount - inProgressCount
  );

  const coverImage =
    property.images && property.images.length > 0 ? property.images[0] : null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className="group bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md hover:border-blue-400 cursor-pointer flex flex-col h-full"
      aria-label={`View ${requestsCount} maintenance requests for ${property.title}`}
    >
      {/* SECTION 1: Visual Context */}
      <div className="relative h-32 w-full bg-gray-100 border-b border-gray-100">
        {coverImage ? (
          <img
            src={coverImage}
            alt={property.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-300">
            <PhotoIcon className="w-12 h-12" />
          </div>
        )}

        {/* Overlay Badge for Total Requests */}
        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm text-xs font-bold text-gray-700 border border-gray-200">
          {requestsCount} Requests
        </div>
      </div>

      {/* SECTION 2: Property Info */}
      <div className="p-4 flex-grow flex flex-col justify-between">
        <div className="flex justify-between items-start gap-2">
          <div>
            <h3 className="text-base font-bold text-gray-900 line-clamp-2 leading-tight">
              {property.title}
            </h3>
            {/* Show location/city to disambiguate properties with similar names */}
            <p className="text-xs text-gray-500 mt-1 truncate">
              {property.location}
            </p>
          </div>
          <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
        </div>


        <div className="mt-5 grid grid-cols-3 gap-2">
          {/* Pending: High Contrast (Action Needed) */}
          <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-orange-50 border border-orange-100">
            <ClockIcon className="w-4 h-4 text-orange-500 mb-1" />
            <span className="text-lg font-bold text-gray-800 leading-none">
              {pendingCount}
            </span>
            <span className="text-[10px] uppercase font-semibold text-orange-600 mt-1">
              Pending
            </span>
          </div>

          {/* In Progress: Blue (Active) */}
          <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-blue-50 border border-blue-100">
            <WrenchScrewdriverIcon className="w-4 h-4 text-blue-500 mb-1" />
            <span className="text-lg font-bold text-gray-800 leading-none">
              {inProgressCount}
            </span>
            <span className="text-[10px] uppercase font-semibold text-blue-600 mt-1">
              Active
            </span>
          </div>

          {/* Completed: Green/Gray (History) */}
          <div className="flex flex-col items-center justify-center p-2 rounded-lg bg-gray-50 border border-gray-100">
            <CheckCircleIcon className="w-4 h-4 text-green-500 mb-1" />
            <span className="text-lg font-bold text-gray-800 leading-none">
              {completedCount}
            </span>
            <span className="text-[10px] uppercase font-semibold text-gray-500 mt-1">
              Done
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyMaintenanceSummaryCard;
